import { Loader2 } from 'lucide-preact'
import { ToggleRow } from '../common/ToggleRow'
import type { AllCustomersAccessChangeDto } from '../../api/userRoles'
import type { RoleType } from '../../types/userRoles'
import type { useAllCustomersAccess } from './useAllCustomersAccess'

const ROLES: RoleType[] = ['Viewer', 'Editor', 'Admin']

interface AllCustomersAccessSectionProps {
  access: ReturnType<typeof useAllCustomersAccess>
  managingCompanyName: string
  firstName: string
}

const customers = (count: number) => `${count} ${count === 1 ? 'customer' : 'customers'}`
const names = (list: string[]) => list.join(', ')

/**
 * What saving the switch would do, from the server's preview. Every line names customers, so the
 * admin sees the consequence before Save rather than finding it on someone else's Users page.
 */
function Impact({ preview, managingCompanyName, firstName }: { preview: AllCustomersAccessChangeDto; managingCompanyName: string; firstName: string }) {
  const lines: preact.ComponentChildren[] = []

  if (preview.Enabled) {
    const reached = preview.Added.length + preview.Replaced.length + preview.Changed.length
    lines.push(<span>Gives <b>{preview.Role}</b> on {customers(reached)}, and on every customer created later.</span>)
    if (preview.Replaced.length > 0) {
      lines.push(
        <span>
          Replaces existing access on <b>{customers(preview.Replaced.length)}</b>: {names(preview.Replaced)}. Turning the
          switch off gives it back.
        </span>
      )
    }
  } else {
    if (preview.Removed.length > 0 || preview.Restored.length === 0) {
      lines.push(<span>Removes switch-managed access on <b>{customers(preview.Removed.length)}</b>. Access added by hand stays.</span>)
    }
    if (preview.Restored.length > 0) {
      lines.push(
        <span>
          Gives back the access added by hand that the switch replaced on <b>{customers(preview.Restored.length)}</b>:{' '}
          {names(preview.Restored)}.
        </span>
      )
    }
  }

  if (preview.Skipped.length > 0) {
    lines.push(
      <span>
        Skipped: <b>{names(preview.Skipped)}</b>. {firstName} is the last {managingCompanyName} admin there, so their
        access is kept; add another admin first to change it.
      </span>
    )
  }

  return (
    <div className="mt-3 space-y-1 rounded-md bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
      {lines.map((line, index) => <p key={index}>{line}</p>)}
    </div>
  )
}

/**
 * ORB-1019: "Access to all customers", in Edit roles on the managing company's own Users page. Shown
 * only to that company's admins; the endpoint refuses everybody else as well.
 */
export function AllCustomersAccessSection({ access, managingCompanyName, firstName }: AllCustomersAccessSectionProps) {
  const { current, loadError, enabled, setEnabled, role, setRole, preview, previewError, retryPreview, dirty } = access
  const unavailable = current !== null && !current.CanBeSet && !current.Enabled

  return (
    <section className="mb-8" aria-labelledby="all-customers-heading">
      <h3 className="text-sm font-medium text-slate-900 dark:text-white mb-3">Customers</h3>
      <div className="flex items-start gap-4 rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-3.5">
        <div className="flex-1 min-w-0">
          <p id="all-customers-heading" className="text-sm font-medium text-slate-900 dark:text-white">Access to all customers</p>
          <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
            Every customer {managingCompanyName} manages, including ones created later. Customers can see this access
            but cannot change it.
          </p>

          {loadError && <p id="all-customers-load-error" className="mt-2 text-sm text-red-600 dark:text-red-400">{loadError}</p>}
          {!current && !loadError && (
            <p className="mt-2 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Checking…
            </p>
          )}
          {unavailable && (
            <p id="all-customers-unavailable" className="mt-2 text-sm text-amber-700 dark:text-amber-400">{current.UnavailableReason}</p>
          )}

          {current && enabled && (
            <label className="mt-3 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              Role on each customer
              <select
                value={role}
                onChange={(e) => setRole((e.target as HTMLSelectElement).value as RoleType)}
                className="px-2.5 py-1.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {ROLES.map(option => <option key={option} value={option}>{option}</option>)}
              </select>
            </label>
          )}

          {dirty && (
            <div aria-live="polite">
              {preview ? (
                <Impact preview={preview} managingCompanyName={managingCompanyName} firstName={firstName} />
              ) : previewError ? (
                <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                  {previewError}{' '}
                  <button type="button" onClick={retryPreview} className="font-medium underline hover:no-underline">
                    Try again
                  </button>
                </p>
              ) : (
                <p className="mt-3 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin" /> Working out what this changes…
                </p>
              )}
            </div>
          )}

          {current && (
            <p className="mt-3 text-xs tabular-nums text-slate-500 dark:text-slate-400">
              {customers(current.CustomerCount)} managed by {managingCompanyName}
            </p>
          )}
        </div>
        <ToggleRow
          checked={enabled}
          onChange={setEnabled}
          // Turning it off is always allowed; turning it on needs an accepted invite.
          disabled={!current || (!current.CanBeSet && !enabled)}
          ariaLabel="Access to all customers"
          // A disabled switch has to say why, or a screen reader announces only "disabled".
          ariaDescribedBy={loadError ? 'all-customers-load-error' : unavailable ? 'all-customers-unavailable' : undefined}
        />
      </div>
    </section>
  )
}
